// src/pages/AdminAcademiaPage.js
import React, { useState, useEffect } from 'react';

const API      = '/api/admin_academia_save.php';
const GET      = '/api/get_academia_data.php';
const UPLOAD   = '/api/upload_image.php';
const DIAS     = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];

const bs = { fontFamily: "'Montserrat', sans-serif" };
const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

// ── helpers ────────────────────────────────────────────────────────────────
const post = async (type, payload) => {
  const form = new FormData();
  form.append('type', type);
  Object.entries(payload).forEach(([k, v]) => form.append(k, v ?? ''));
  const res  = await fetch(API, { method: 'POST', body: form });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Error al guardar');
  return json.insert_id;
};

const del = async (type, id) => {
  const form = new FormData();
  form.append('type', `delete_${type}`);
  form.append('id', id);
  const res  = await fetch(API, { method: 'POST', body: form });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Error al eliminar');
};

const uploadImg = async (file) => {
  const form = new FormData();
  form.append('image', file);
  const res  = await fetch(UPLOAD, { method: 'POST', body: form, credentials: 'include' });
  const json = await res.json();
  if (!json.success) throw new Error(json.message || 'Error subiendo imagen');
  return json.data.url;
};

// ── sub-components ─────────────────────────────────────────────────────────
const SectionHeader = ({ title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
    <h2 style={{ ...ds, fontSize: '1.4rem', fontWeight: 600, color: '#060D14', margin: 0 }}>{title}</h2>
    <div style={{ flex: 1, height: '1px', background: '#f0eaea' }} />
  </div>
);

const inp = {
  ...bs, padding: '9px 12px', border: '1px solid #e5e0e0', fontSize: '12px',
  color: '#060D14', outline: 'none', borderRadius: '2px', background: 'white',
  transition: 'border-color 0.2s',
};

const EditBtn = ({ onClick }) => (
  <button type="button" onClick={onClick} style={{ background: 'none', border: 'none',
    cursor: 'pointer', color: '#bbb', padding: '2px', transition: 'color 0.15s' }}
  onMouseOver={e => e.currentTarget.style.color = '#961914'}
  onMouseOut={e => e.currentTarget.style.color = '#bbb'}>
    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
    </svg>
  </button>
);

const DelBtn = ({ onClick }) => (
  <button type="button" onClick={onClick} style={{ background: 'none', border: 'none',
    cursor: 'pointer', color: '#ddd', padding: '2px', transition: 'color 0.15s' }}
  onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
  onMouseOut={e => e.currentTarget.style.color = '#ddd'}>
    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
    </svg>
  </button>
);

const SaveBtn = ({ saving, label = 'Guardar' }) => (
  <button type="submit" disabled={saving} style={{
    ...bs, background: '#961914', color: 'white', border: 'none',
    padding: '9px 20px', cursor: saving ? 'not-allowed' : 'pointer',
    fontSize: '10px', fontWeight: 600, letterSpacing: '0.15em',
    textTransform: 'uppercase', borderRadius: '2px', opacity: saving ? 0.7 : 1,
    transition: 'background 0.2s', whiteSpace: 'nowrap',
  }}
  onMouseOver={e => { if (!saving) e.currentTarget.style.background = '#7a140f'; }}
  onMouseOut={e => e.currentTarget.style.background = '#961914'}>
    {saving ? 'Guardando…' : label}
  </button>
);

const CancelBtn = ({ onClick }) => (
  <button type="button" onClick={onClick} style={{
    ...bs, background: 'none', border: '1px solid #e5e0e0', color: '#666',
    padding: '9px 16px', cursor: 'pointer', fontSize: '10px', fontWeight: 600,
    letterSpacing: '0.12em', textTransform: 'uppercase', borderRadius: '2px',
    transition: 'all 0.2s', whiteSpace: 'nowrap',
  }}
  onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
  onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e0e0'; e.currentTarget.style.color = '#666'; }}>
    Cancelar
  </button>
);

const Card = ({ children }) => (
  <div style={{ background: 'white', border: '1px solid #f0eaea',
    borderRadius: '2px', padding: '24px', marginBottom: '16px' }}>
    {children}
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────
const AdminAcademiaPage = () => {
  const [loading,      setLoading]      = useState(true);
  const [disciplinas,  setDisciplinas]  = useState([]);
  const [niveles,      setNiveles]      = useState([]);
  const [maestros,     setMaestros]     = useState([]);
  const [horarios,     setHorarios]     = useState([]);
  const [costos,       setCostos]       = useState([]);

  const [editDisc,  setEditDisc]  = useState(null);
  const [editLevel, setEditLevel] = useState(null);
  const [editTeach, setEditTeach] = useState(null);
  const [editSched, setEditSched] = useState(null);
  const [editCost,  setEditCost]  = useState(null);

  const [newDisc,  setNewDisc]  = useState('');
  const [newLevel, setNewLevel] = useState('');
  const [newTeach, setNewTeach] = useState({ nombre: '', descripcion: '', foto_url: '' });
  const [newSched, setNewSched] = useState({ nivel_id: '', maestro_id: '', disciplina_id: '',
    dia_semana: '', hora_inicio: '', hora_fin: '', horario: '' });
  const [newCost,  setNewCost]  = useState({ descripcion: '', monto: '' });

  const [uploadingTeach,    setUploadingTeach]    = useState(false);
  const [uploadingEditTeach, setUploadingEditTeach] = useState(false);
  const [saving,            setSaving]            = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(GET);
        const json = await res.json();
        if (!json.error) {
          setDisciplinas(json.disciplinas || []);
          setNiveles(json.niveles         || []);
          setMaestros(json.maestros        || []);
          setHorarios(json.horarios        || []);
          setCostos(json.costos            || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  // ── Foto maestro helpers ─────────────────────────────────────────────────
  const handleTeachPhoto = async (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    isEdit ? setUploadingEditTeach(true) : setUploadingTeach(true);
    try {
      const url = await uploadImg(file);
      if (isEdit) setEditTeach(p => ({ ...p, foto_url: url }));
      else        setNewTeach(p => ({ ...p, foto_url: url }));
    } catch (err) { alert(err.message); }
    finally {
      isEdit ? setUploadingEditTeach(false) : setUploadingTeach(false);
    }
  };

  // ── DISCIPLINAS ──────────────────────────────────────────────────────────
  const addDisc = async (e) => {
    e.preventDefault(); if (!newDisc.trim()) return;
    setSaving('disc');
    try {
      const id = await post('disciplina', { nombre: newDisc.trim() });
      setDisciplinas(p => [...p, { id, nombre: newDisc.trim() }]);
      setNewDisc('');
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };
  const saveDisc = async (e) => {
    e.preventDefault();
    setSaving('disc');
    try {
      await post('edit_disciplina', { id: editDisc.id, nombre: editDisc.nombre });
      setDisciplinas(p => p.map(x => x.id === editDisc.id ? editDisc : x));
      setEditDisc(null);
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };

  // ── NIVELES ──────────────────────────────────────────────────────────────
  const addLevel = async (e) => {
    e.preventDefault(); if (!newLevel.trim()) return;
    setSaving('level');
    try {
      const id = await post('nivel', { nombre: newLevel.trim() });
      setNiveles(p => [...p, { id, nombre: newLevel.trim() }]);
      setNewLevel('');
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };
  const saveLevel = async (e) => {
    e.preventDefault();
    setSaving('level');
    try {
      await post('edit_nivel', { id: editLevel.id, nombre: editLevel.nombre });
      setNiveles(p => p.map(x => x.id === editLevel.id ? editLevel : x));
      setEditLevel(null);
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };

  // ── MAESTROS ─────────────────────────────────────────────────────────────
  const addTeach = async (e) => {
    e.preventDefault(); if (!newTeach.nombre.trim()) return;
    setSaving('teach');
    try {
      const id = await post('maestro', newTeach);
      setMaestros(p => [...p, { id, ...newTeach }]);
      setNewTeach({ nombre: '', descripcion: '', foto_url: '' });
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };
  const saveTeach = async (e) => {
    e.preventDefault();
    setSaving('teach');
    try {
      await post('edit_maestro', { id: editTeach.id, nombre: editTeach.nombre,
        descripcion: editTeach.descripcion || '', foto_url: editTeach.foto_url || '' });
      setMaestros(p => p.map(x => x.id === editTeach.id ? editTeach : x));
      setEditTeach(null);
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };

  // ── HORARIOS ─────────────────────────────────────────────────────────────
  const addSched = async (e) => {
    e.preventDefault();
    if (!newSched.dia_semana && !newSched.horario) { alert('Selecciona un día o escribe el horario.'); return; }
    setSaving('sched');
    try {
      const id = await post('horario', newSched);
      const disc = disciplinas.find(d => String(d.id) === String(newSched.disciplina_id));
      const niv  = niveles.find(n => String(n.id) === String(newSched.nivel_id));
      const mae  = maestros.find(m => String(m.id) === String(newSched.maestro_id));
      setHorarios(p => [...p, { id, ...newSched,
        disciplina: disc?.nombre || '', nivel: niv?.nombre || '', maestro: mae?.nombre || '' }]);
      setNewSched({ nivel_id: '', maestro_id: '', disciplina_id: '',
        dia_semana: '', hora_inicio: '', hora_fin: '', horario: '' });
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };
  const saveSched = async (e) => {
    e.preventDefault();
    setSaving('sched');
    try {
      await post('edit_horario', { id: editSched.id, nivel_id: editSched.nivel_id,
        maestro_id: editSched.maestro_id, disciplina_id: editSched.disciplina_id,
        dia_semana: editSched.dia_semana, hora_inicio: editSched.hora_inicio,
        hora_fin: editSched.hora_fin, horario: editSched.horario });
      const disc = disciplinas.find(d => String(d.id) === String(editSched.disciplina_id));
      const niv  = niveles.find(n => String(n.id) === String(editSched.nivel_id));
      const mae  = maestros.find(m => String(m.id) === String(editSched.maestro_id));
      setHorarios(p => p.map(x => x.id === editSched.id
        ? { ...editSched, disciplina: disc?.nombre || '', nivel: niv?.nombre || '', maestro: mae?.nombre || '' }
        : x));
      setEditSched(null);
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };

  // ── COSTOS ───────────────────────────────────────────────────────────────
  const addCost = async (e) => {
    e.preventDefault(); if (!newCost.descripcion.trim() || !newCost.monto) return;
    setSaving('cost');
    try {
      const id = await post('costo', newCost);
      setCostos(p => [...p, { id, ...newCost }]);
      setNewCost({ descripcion: '', monto: '' });
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };
  const saveCost = async (e) => {
    e.preventDefault();
    setSaving('cost');
    try {
      await post('edit_costo', { id: editCost.id, descripcion: editCost.descripcion, monto: editCost.monto });
      setCostos(p => p.map(x => x.id === editCost.id ? editCost : x));
      setEditCost(null);
    } catch (err) { alert(err.message); }
    finally { setSaving(''); }
  };

  const SchedFormFields = ({ data, setData }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: '10px' }}>
      <div>
        <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
          textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>Disciplina</label>
        <select value={data.disciplina_id || ''}
          onChange={e => setData(p => ({ ...p, disciplina_id: e.target.value }))}
          style={{ ...inp, width: '100%' }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'}>
          <option value="">— opcional —</option>
          {disciplinas.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
        </select>
      </div>
      <div>
        <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
          textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>Nivel</label>
        <select value={data.nivel_id || ''}
          onChange={e => setData(p => ({ ...p, nivel_id: e.target.value }))}
          style={{ ...inp, width: '100%' }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'}>
          <option value="">— opcional —</option>
          {niveles.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
        </select>
      </div>
      <div>
        <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
          textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>Maestro</label>
        <select value={data.maestro_id || ''}
          onChange={e => setData(p => ({ ...p, maestro_id: e.target.value }))}
          style={{ ...inp, width: '100%' }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'}>
          <option value="">— opcional —</option>
          {maestros.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
        </select>
      </div>
      <div>
        <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
          textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>Día</label>
        <select value={data.dia_semana || ''}
          onChange={e => setData(p => ({ ...p, dia_semana: e.target.value }))}
          style={{ ...inp, width: '100%' }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'}>
          <option value="">— opcional —</option>
          {DIAS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <div>
        <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
          textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>Hora inicio</label>
        <input type="time" value={data.hora_inicio || ''}
          onChange={e => setData(p => ({ ...p, hora_inicio: e.target.value }))}
          style={{ ...inp, width: '100%' }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
      </div>
      <div>
        <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
          textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>Hora fin</label>
        <input type="time" value={data.hora_fin || ''}
          onChange={e => setData(p => ({ ...p, hora_fin: e.target.value }))}
          style={{ ...inp, width: '100%' }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
          textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>
          Texto libre (ej: "Lunes y Miércoles 4–5pm")
        </label>
        <input type="text" value={data.horario || ''}
          onChange={e => setData(p => ({ ...p, horario: e.target.value }))}
          placeholder="Texto descriptivo opcional"
          style={{ ...inp, width: '100%', boxSizing: 'border-box' }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ padding: '48px', textAlign: 'center' }}>
      <p style={{ ...bs, fontSize: '12px', color: '#bbb', letterSpacing: '0.15em',
        textTransform: 'uppercase' }}>Cargando academia…</p>
    </div>
  );

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px',
      display: 'flex', flexDirection: 'column', gap: '24px' }}>

      {/* ── DISCIPLINAS ───────────────────────────────────────────────── */}
      <Card>
        <SectionHeader title="Disciplinas" />
        <form onSubmit={editDisc ? saveDisc : addDisc}
          style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <input type="text" required
            value={editDisc ? editDisc.nombre : newDisc}
            onChange={e => editDisc ? setEditDisc(p => ({ ...p, nombre: e.target.value })) : setNewDisc(e.target.value)}
            placeholder="Nombre de la disciplina"
            style={{ ...inp, flex: 1 }}
            onFocus={e => e.target.style.borderColor = '#961914'}
            onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          <SaveBtn saving={saving === 'disc'} label={editDisc ? 'Actualizar' : 'Agregar'} />
          {editDisc && <CancelBtn onClick={() => setEditDisc(null)} />}
        </form>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {disciplinas.map((d, i) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', padding: '10px 14px',
              background: i % 2 === 0 ? '#faf9f9' : 'white',
              border: '1px solid #f5f0f0', borderRadius: '2px' }}>
              <span style={{ ...ds, fontSize: '1rem', fontWeight: 600, color: '#060D14' }}>{d.nombre}</span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <EditBtn onClick={() => setEditDisc({ ...d })} />
                <DelBtn onClick={async () => { if (!window.confirm('¿Eliminar?')) return; await del('disciplina', d.id); setDisciplinas(p => p.filter(x => x.id !== d.id)); }} />
              </div>
            </div>
          ))}
          {!disciplinas.length && <p style={{ ...bs, fontSize: '12px', color: '#bbb', textAlign: 'center', padding: '12px 0' }}>Sin disciplinas.</p>}
        </div>
      </Card>

      {/* ── NIVELES ───────────────────────────────────────────────────── */}
      <Card>
        <SectionHeader title="Niveles" />
        <form onSubmit={editLevel ? saveLevel : addLevel}
          style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <input type="text" required
            value={editLevel ? editLevel.nombre : newLevel}
            onChange={e => editLevel ? setEditLevel(p => ({ ...p, nombre: e.target.value })) : setNewLevel(e.target.value)}
            placeholder="Nombre del nivel"
            style={{ ...inp, flex: 1 }}
            onFocus={e => e.target.style.borderColor = '#961914'}
            onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          <SaveBtn saving={saving === 'level'} label={editLevel ? 'Actualizar' : 'Agregar'} />
          {editLevel && <CancelBtn onClick={() => setEditLevel(null)} />}
        </form>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {niveles.map(n => (
            <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: '4px',
              padding: '6px 12px', border: '1px solid #e5c9c9', borderRadius: '20px',
              background: 'white' }}>
              {editLevel?.id === n.id ? (
                <>
                  <input value={editLevel.nombre}
                    onChange={e => setEditLevel(p => ({ ...p, nombre: e.target.value }))}
                    style={{ ...inp, width: '120px', padding: '4px 8px', fontSize: '12px' }}
                    onFocus={e => e.target.style.borderColor = '#961914'}
                    onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
                  <button onClick={saveLevel} style={{ ...bs, background: '#961914', color: 'white',
                    border: 'none', padding: '4px 10px', cursor: 'pointer', fontSize: '10px',
                    borderRadius: '2px' }}>✓</button>
                  <button onClick={() => setEditLevel(null)} style={{ background: 'none',
                    border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '14px' }}>✕</button>
                </>
              ) : (
                <>
                  <span style={{ ...bs, fontSize: '11px', fontWeight: 600, color: '#961914' }}>{n.nombre}</span>
                  <EditBtn onClick={() => setEditLevel({ ...n })} />
                  <DelBtn onClick={async () => { if (!window.confirm('¿Eliminar?')) return; await del('nivel', n.id); setNiveles(p => p.filter(x => x.id !== n.id)); }} />
                </>
              )}
            </div>
          ))}
          {!niveles.length && <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>Sin niveles.</p>}
        </div>
      </Card>

      {/* ── MAESTROS ──────────────────────────────────────────────────── */}
      <Card>
        <SectionHeader title="Maestros" />
        {/* Formulario nuevo maestro */}
        <form onSubmit={editTeach ? saveTeach : addTeach}
          style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
                textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>Nombre *</label>
              <input type="text" required
                value={editTeach ? editTeach.nombre : newTeach.nombre}
                onChange={e => editTeach ? setEditTeach(p => ({ ...p, nombre: e.target.value })) : setNewTeach(p => ({ ...p, nombre: e.target.value }))}
                style={{ ...inp, width: '100%', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = '#961914'}
                onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
            </div>
            {/* Foto */}
            <div>
              <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
                textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>
                Foto {(uploadingTeach || uploadingEditTeach) && '— subiendo…'}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {(editTeach ? editTeach.foto_url : newTeach.foto_url) && (
                  <img src={editTeach ? editTeach.foto_url : newTeach.foto_url}
                    alt="" style={{ width: '40px', height: '40px', objectFit: 'cover',
                      borderRadius: '50%', border: '2px solid #e5c9c9' }} />
                )}
                <label style={{ ...bs, cursor: 'pointer', padding: '8px 14px',
                  border: '1px dashed #e5c9c9', borderRadius: '2px', fontSize: '11px',
                  color: '#961914', transition: 'all 0.2s', display: 'inline-block' }}>
                  {(editTeach ? editTeach.foto_url : newTeach.foto_url) ? 'Cambiar foto' : 'Subir foto'}
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => handleTeachPhoto(e, !!editTeach)} />
                </label>
              </div>
            </div>
          </div>
          <div>
            <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
              textTransform: 'uppercase', color: '#999', marginBottom: '4px' }}>Descripción</label>
            <textarea rows={2}
              value={editTeach ? (editTeach.descripcion || '') : newTeach.descripcion}
              onChange={e => editTeach ? setEditTeach(p => ({ ...p, descripcion: e.target.value })) : setNewTeach(p => ({ ...p, descripcion: e.target.value }))}
              style={{ ...inp, width: '100%', resize: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = '#961914'}
              onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <SaveBtn saving={saving === 'teach'} label={editTeach ? 'Actualizar Maestro' : 'Agregar Maestro'} />
            {editTeach && <CancelBtn onClick={() => setEditTeach(null)} />}
          </div>
        </form>

        {/* Lista maestros */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {maestros.map((m, i) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '12px',
              padding: '12px 14px', background: i % 2 === 0 ? '#faf9f9' : 'white',
              border: '1px solid #f5f0f0', borderRadius: '2px' }}>
              {/* Foto */}
              <div style={{ width: '44px', height: '44px', borderRadius: '50%',
                overflow: 'hidden', background: '#f5f2f2', flexShrink: 0,
                border: '2px solid #e5c9c9' }}>
                {m.foto_url
                  ? <img src={m.foto_url} alt={m.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: '#ddd' }}>
                      <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                }
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ ...ds, fontSize: '1rem', fontWeight: 600, color: '#060D14', margin: '0 0 2px' }}>{m.nombre}</p>
                {m.descripcion && <p style={{ ...bs, fontSize: '11px', color: '#aaa', margin: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.descripcion}</p>}
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <EditBtn onClick={() => setEditTeach({ ...m, foto_url: m.foto_url || '' })} />
                <DelBtn onClick={async () => { if (!window.confirm('¿Eliminar?')) return; await del('maestro', m.id); setMaestros(p => p.filter(x => x.id !== m.id)); }} />
              </div>
            </div>
          ))}
          {!maestros.length && <p style={{ ...bs, fontSize: '12px', color: '#bbb', textAlign: 'center', padding: '12px 0' }}>Sin maestros.</p>}
        </div>
      </Card>

      {/* ── HORARIOS ──────────────────────────────────────────────────── */}
      <Card>
        <SectionHeader title="Horarios" />
        <form onSubmit={editSched ? saveSched : addSched}
          style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <SchedFormFields data={editSched || newSched} setData={editSched ? setEditSched : setNewSched} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <SaveBtn saving={saving === 'sched'} label={editSched ? 'Actualizar' : 'Agregar Horario'} />
            {editSched && <CancelBtn onClick={() => setEditSched(null)} />}
          </div>
        </form>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {horarios.map((h, i) => (
            <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: '12px',
              padding: '10px 14px', background: i % 2 === 0 ? '#faf9f9' : 'white',
              border: '1px solid #f5f0f0', borderRadius: '2px' }}>
              <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                {h.dia_semana && (
                  <span style={{ ...bs, fontSize: '10px', fontWeight: 700, color: 'white',
                    background: '#961914', padding: '3px 10px', borderRadius: '2px',
                    letterSpacing: '0.1em', textTransform: 'uppercase' }}>{h.dia_semana}</span>
                )}
                {(h.hora_inicio || h.hora_fin) && (
                  <span style={{ ...bs, fontSize: '12px', fontWeight: 600, color: '#060D14' }}>
                    {h.hora_inicio} – {h.hora_fin}
                  </span>
                )}
                {h.disciplina && (
                  <span style={{ ...bs, fontSize: '11px', color: '#961914', fontWeight: 600 }}>{h.disciplina}</span>
                )}
                {h.nivel && <span style={{ ...bs, fontSize: '11px', color: '#888' }}>{h.nivel}</span>}
                {h.maestro && <span style={{ ...bs, fontSize: '11px', color: '#bbb' }}>{h.maestro}</span>}
                {h.horario && !h.dia_semana && (
                  <span style={{ ...bs, fontSize: '11px', color: '#888' }}>{h.horario}</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <EditBtn onClick={() => setEditSched({
                  id: h.id, nivel_id: h.nivel_id || '', maestro_id: h.maestro_id || '',
                  disciplina_id: h.disciplina_id || '', dia_semana: h.dia_semana || '',
                  hora_inicio: h.hora_inicio || '', hora_fin: h.hora_fin || '', horario: h.horario || ''
                })} />
                <DelBtn onClick={async () => { if (!window.confirm('¿Eliminar?')) return; await del('horario', h.id); setHorarios(p => p.filter(x => x.id !== h.id)); }} />
              </div>
            </div>
          ))}
          {!horarios.length && <p style={{ ...bs, fontSize: '12px', color: '#bbb', textAlign: 'center', padding: '12px 0' }}>Sin horarios.</p>}
        </div>
      </Card>

      {/* ── COSTOS ────────────────────────────────────────────────────── */}
      <Card>
        <SectionHeader title="Costos" />
        <form onSubmit={editCost ? saveCost : addCost}
          style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input type="text" required
            value={editCost ? editCost.descripcion : newCost.descripcion}
            onChange={e => editCost ? setEditCost(p => ({ ...p, descripcion: e.target.value })) : setNewCost(p => ({ ...p, descripcion: e.target.value }))}
            placeholder="Descripción del plan"
            style={{ ...inp, flex: '2', minWidth: '180px' }}
            onFocus={e => e.target.style.borderColor = '#961914'}
            onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          <input type="number" required min="0"
            value={editCost ? editCost.monto : newCost.monto}
            onChange={e => editCost ? setEditCost(p => ({ ...p, monto: e.target.value })) : setNewCost(p => ({ ...p, monto: e.target.value }))}
            placeholder="$ Monto"
            style={{ ...inp, width: '100px' }}
            onFocus={e => e.target.style.borderColor = '#961914'}
            onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          <SaveBtn saving={saving === 'cost'} label={editCost ? 'Actualizar' : 'Agregar'} />
          {editCost && <CancelBtn onClick={() => setEditCost(null)} />}
        </form>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {costos.map((c, i) => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', padding: '10px 14px',
              background: i % 2 === 0 ? '#faf9f9' : 'white',
              border: '1px solid #f5f0f0', borderRadius: '2px' }}>
              <span style={{ ...bs, fontSize: '13px', color: '#060D14' }}>{c.descripcion}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ ...ds, fontSize: '1.2rem', color: '#961914' }}>${Number(c.monto).toFixed(2)}</span>
                <EditBtn onClick={() => setEditCost({ ...c })} />
                <DelBtn onClick={async () => { if (!window.confirm('¿Eliminar?')) return; await del('costo', c.id); setCostos(p => p.filter(x => x.id !== c.id)); }} />
              </div>
            </div>
          ))}
          {!costos.length && <p style={{ ...bs, fontSize: '12px', color: '#bbb', textAlign: 'center', padding: '12px 0' }}>Sin costos.</p>}
        </div>
      </Card>

    </div>
  );
};

export default AdminAcademiaPage;
